import { Printer, Download, FileText, RefreshCw } from 'lucide-react';
import { Spinner } from '@/components/ui/Spinner';
import { cn } from '@/utils/cn';
import { logReportAction } from '@/api/reportView.api';

interface ReportToolbarProps {
  /** Called when user clicks "Export Excel" */
  onExcelExport?: () => void;
  /** Called when user clicks "CSV" */
  onCsvExport?: () => void;
  /** Called when user clicks "Print" */
  onPrint?: () => void;
  /** Loading indicator shown on the refresh/run button */
  isLoading?: boolean;
  /** Whether export/print are available (data has been loaded) */
  hasData?: boolean;
  /** When set, export/print actions are audit-logged fire-and-forget */
  reportKey?: string;
  /** Row count reported to the export log */
  rowCount?: number;
  /** Human-readable filter summary reported to the export log */
  filterSummary?: string;
  /** Extra class for the wrapper */
  className?: string;
  /** Children: the primary Run/Generate button */
  children?: React.ReactNode;
}

const btnBase =
  'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed';

export const ReportToolbar = ({
  onExcelExport,
  onCsvExport,
  onPrint,
  isLoading = false,
  hasData = false,
  reportKey,
  rowCount,
  filterSummary,
  className,
  children,
}: ReportToolbarProps) => {
  const log = (action: 'EXPORT_EXCEL' | 'EXPORT_CSV' | 'PRINT') => {
    if (reportKey) logReportAction(reportKey, action, rowCount ?? 0, filterSummary);
  };

  const handleExcel = onExcelExport
    ? () => { onExcelExport(); log('EXPORT_EXCEL'); }
    : undefined;
  const handleCsv = onCsvExport
    ? () => { onCsvExport(); log('EXPORT_CSV'); }
    : undefined;
  const handlePrint = onPrint
    ? () => { onPrint(); log('PRINT'); }
    : undefined;

  return (
    <div className={cn('flex flex-wrap items-center gap-2', className)}>
      {/* Primary action slot (Run / Generate button from parent) */}
      {children}

      {/* ── Secondary actions ── only enabled when data is loaded ── */}
      {handleExcel && (
        <button
          onClick={handleExcel}
          disabled={!hasData || isLoading}
          className={cn(btnBase,
            'border border-green-600 text-green-700 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20'
          )}
          title="Export to Excel (.xlsx)"
        >
          <Download size={14} />
          Excel
        </button>
      )}

      {handleCsv && (
        <button
          onClick={handleCsv}
          disabled={!hasData || isLoading}
          className={cn(btnBase,
            'border border-teal-600 text-teal-700 dark:text-teal-400 hover:bg-teal-50 dark:hover:bg-teal-900/20'
          )}
          title="Export to CSV (.csv)"
        >
          <FileText size={14} />
          CSV
        </button>
      )}

      {handlePrint && (
        <button
          onClick={handlePrint}
          disabled={!hasData || isLoading}
          className={cn(btnBase,
            'border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'
          )}
          title="Print report"
        >
          <Printer size={14} />
          Print
        </button>
      )}

      {isLoading && <Spinner size="sm" />}
    </div>
  );
};

/** Primary "Run Report" button used inside ReportToolbar children */
export const RunReportButton = ({
  onClick,
  disabled,
  isLoading,
  label = 'Run Report',
}: {
  onClick: () => void;
  disabled?: boolean;
  isLoading?: boolean;
  label?: string;
}) => (
  <button
    onClick={onClick}
    disabled={disabled || isLoading}
    className="flex items-center gap-2 px-4 py-1.5 bg-brand-600 text-white rounded-lg text-sm font-medium hover:bg-brand-700 disabled:opacity-50"
  >
    {isLoading ? <Spinner size="sm" /> : <RefreshCw size={14} />}
    {label}
  </button>
);
